/**
 * User Behaviour Analytics — Settings > User Analytics
 * Super-admin only. Shows adoption, activity, module usage, login trends.
 */
import React, { useState, useEffect } from 'react';
import { apiGet, api } from '../../lib/api';
import { toast } from 'sonner';
import {
  Download, RefreshCw, Users, Activity, TrendingUp,
  Eye, LogIn, AlertCircle, ChevronRight,
} from 'lucide-react';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;
const daysAgo = (ds) => {
  if (!ds) return '—';
  const d = Math.floor((Date.now() - new Date(ds)) / 86400000);
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  return `${d}d ago`;
};

function KPITile({ label, value, sub, accent = 'indigo', icon: Icon }) {
  const colours = {
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div className={`rounded-md border p-4 ${colours[accent]}`}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} />}
        <span className="text-[10px] font-display font-bold uppercase tracking-wider opacity-70">{label}</span>
      </div>
      <div className="text-2xl font-display font-extrabold">{value}</div>
      {sub && <div className="text-[11px] mt-0.5 opacity-60">{sub}</div>}
    </div>
  );
}

function Bar({ value, max, colour = '#3b4872' }) {
  const w = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1">
      <div className="h-1.5 rounded-full" style={{ width: `${w}%`, background: colour }} />
    </div>
  );
}

export default function UserAnalytics() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState(null);
  const [depts, setDepts] = useState([]);
  const [trend, setTrend] = useState([]);
  const [activeTab, setActiveTab] = useUrlTab('tab', 'overview');
  const [sortBy, setSortBy] = useState('login_count');
  const [downloading, setDownloading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, m, d, t] = await Promise.all([
        apiGet('/user-analytics/summary', { days }),
        apiGet('/user-analytics/users', { days, sort_by: sortBy }),
        apiGet('/user-analytics/modules', { days }),
        apiGet('/user-analytics/departments', { days }),
        apiGet('/user-analytics/login-trend', { days }),
      ]);
      setSummary(s);
      setUsers(u.users || []);
      setModules(m);
      setDepts(d.departments || []);
      setTrend(t.trend || []);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [days, sortBy]); // eslint-disable-line

  const loadUserDetail = async (uid) => {
    if (selectedUserId === uid) { setSelectedUserId(null); setUserDetail(null); return; }
    setDetailLoading(true);
    try {
      const d = await apiGet(`/user-analytics/users/${uid}`, { days });
      setUserDetail(d);
      setSelectedUserId(uid);
    } catch { toast.error('Failed to load user detail'); }
    finally { setDetailLoading(false); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const resp = await api.get(`/user-analytics/export.xlsx`, {
        params: { days },
        responseType: 'blob',
        timeout: 60000,
      });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-analytics-${days}d.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error('Download failed'); }
    finally { setDownloading(false); }
  };

  const TabBtn = ({ id, label }) => (
    <button
      onClick={() => setActiveTab(id)}
      data-testid={`ua-tab-${id}`}
      className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-wider border-b-2 transition-colors ${
        activeTab === id
          ? 'border-[#3b4872] text-[#3b4872]'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      {label}
    </button>
  );

  const maxLogins = Math.max(...users.map(u => u.login_count), 1);
  const maxActions = Math.max(...users.map(u => u.total_actions), 1);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold text-[#0F172A]">User Behaviour Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">
            How your team uses the ERP — adoption, activity, and module engagement.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs font-display bg-white"
            data-testid="ua-days-filter"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last 1 year</option>
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-sm text-xs font-display hover:bg-slate-50 disabled:opacity-40"
            data-testid="ua-refresh-btn"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={download}
            disabled={downloading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold hover:bg-[#2d3856] disabled:opacity-40"
            data-testid="ua-download-btn"
          >
            <Download size={12} /> {downloading ? 'Downloading…' : 'Download Excel'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-6 gap-0 overflow-x-auto" data-testid="ua-tabs">
        <TabBtn id="overview" label="Overview" />
        <TabBtn id="users" label="Users" />
        <TabBtn id="modules" label="Modules" />
        <TabBtn id="departments" label="Departments" />
        <TabBtn id="trend" label="Login Trend" />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-400 text-sm">
          <RefreshCw size={16} className="animate-spin mr-2" /> Loading analytics…
        </div>
      )}

      {/* OVERVIEW */}
      {!loading && activeTab === 'overview' && summary && (
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="ua-kpi-grid">
            <KPITile label="Total Users" value={fmt(summary.total_users)} icon={Users} accent="indigo" />
            <KPITile label="Active Users" value={fmt(summary.active_users)} sub={`in last ${days}d`} icon={Activity} accent="emerald" />
            <KPITile label="Adoption Rate" value={pct(summary.adoption_rate_pct)} sub={`${summary.active_users} of ${summary.total_users}`} icon={TrendingUp} accent="indigo" />
            <KPITile label="Currently Online" value={fmt(summary.currently_online)} icon={Eye} accent="emerald" />
            <KPITile label="Active Today (DAU)" value={fmt(summary.daily_active_users)} icon={LogIn} accent="indigo" />
            <KPITile label="Never Logged In" value={fmt(summary.never_logged_in)} icon={AlertCircle} accent={summary.never_logged_in > 0 ? 'amber' : 'slate'} />
            <KPITile label="Total Logins" value={fmt(summary.total_logins)} sub={`last ${days}d`} accent="slate" />
            <KPITile label="Total Actions" value={fmt(summary.total_actions)} sub="all modules" accent="slate" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-slate-200 rounded-md p-4">
              <h3 className="font-display font-bold text-sm mb-3">Top 5 Most Active Users</h3>
              {users.slice(0, 5).map(u => (
                <div key={u.user_id} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-0">
                  <div className="w-7 h-7 rounded-full bg-[#3b4872] text-white text-xs flex items-center justify-center font-bold shrink-0">
                    {(u.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">{u.name}</div>
                    <div className="text-[10px] text-slate-400">{u.department}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs font-bold text-[#3b4872]">{fmt(u.login_count)} logins</div>
                    <div className="text-[10px] text-slate-400">{fmt(u.total_actions)} actions</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border border-slate-200 rounded-md p-4">
              <h3 className="font-display font-bold text-sm mb-3">Inactive Users (no login in {days}d)</h3>
              {users.filter(u => !u.is_active).slice(0, 6).map(u => (
                <div key={u.user_id} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-0">
                  <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-500 text-xs flex items-center justify-center font-bold shrink-0">
                    {(u.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate text-slate-600">{u.name}</div>
                    <div className="text-[10px] text-slate-400">{u.department}</div>
                  </div>
                  <div className="text-right shrink-0 text-xs text-slate-400">{daysAgo(u.last_login_at)}</div>
                </div>
              ))}
              {users.filter(u => !u.is_active).length === 0 && (
                <div className="text-xs text-emerald-700 text-center py-4">All users have been active recently</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* USERS */}
      {!loading && activeTab === 'users' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-slate-500">{users.length} users</div>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="border border-slate-200 rounded-sm px-2 py-1 text-xs"
              data-testid="ua-sort-select">
              <option value="login_count">Sort by Logins</option>
              <option value="actions">Sort by Actions</option>
              <option value="last_login">Sort by Last Login</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>
          <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="ua-users-table">
            <table className="w-full text-xs">
              <thead className="bg-[#3b4872] text-white">
                <tr>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Dept / Role</th>
                  <th className="px-3 py-2 text-center">Logins</th>
                  <th className="px-3 py-2 text-center">Actions</th>
                  <th className="px-3 py-2 text-center">Modules</th>
                  <th className="px-3 py-2 text-left">Last Login</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <React.Fragment key={u.user_id}>
                    <tr
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => loadUserDetail(u.user_id)}
                      data-testid={`ua-user-row-${u.user_id}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-semibold">{u.name}</div>
                        <div className="text-slate-400 text-[10px]">{u.email}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div>{u.department || '—'}</div>
                        <div className="text-slate-400 text-[10px]">{u.role}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-center font-bold text-[#3b4872]">{u.login_count}</div>
                        <Bar value={u.login_count} max={maxLogins} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-center font-bold">{fmt(u.total_actions)}</div>
                        <Bar value={u.total_actions} max={maxActions} colour="#059669" />
                      </td>
                      <td className="px-3 py-2 text-center">{u.modules_used_count}</td>
                      <td className="px-3 py-2 text-slate-500">{daysAgo(u.last_login_at)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          u.is_active
                            ? 'bg-emerald-100 text-emerald-700'
                            : u.days_since_login === null
                            ? 'bg-slate-100 text-slate-500'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {u.is_active ? 'Active' : u.days_since_login === null ? 'Never' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-400">
                        <ChevronRight size={12} className={selectedUserId === u.user_id ? 'rotate-90' : ''} />
                      </td>
                    </tr>
                    {selectedUserId === u.user_id && (
                      <tr className="bg-slate-50 border-t border-slate-100">
                        <td colSpan={8} className="px-4 py-3">
                          {detailLoading ? (
                            <div className="text-xs text-slate-400 flex items-center gap-2">
                              <RefreshCw size={12} className="animate-spin" /> Loading detail…
                            </div>
                          ) : userDetail ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <div className="text-[10px] font-bold uppercase text-slate-400 mb-2">Module Browsing (from today)</div>
                                {userDetail.module_browsing.length === 0 ? (
                                  <div className="text-xs text-slate-400">No browse data yet — tracking started today</div>
                                ) : userDetail.module_browsing.map(m => (
                                  <div key={m.module} className="flex justify-between py-1 border-b border-slate-100 text-xs">
                                    <span>{m.module}</span>
                                    <span className="font-bold text-[#3b4872]">{fmt(m.api_calls)} calls · {m.days_active}d</span>
                                  </div>
                                ))}
                              </div>
                              <div>
                                <div className="text-[10px] font-bold uppercase text-slate-400 mb-2">Actions by Module</div>
                                {userDetail.actions_by_module.slice(0, 10).map((a, i) => (
                                  <div key={i} className="flex justify-between py-1 border-b border-slate-100 text-xs">
                                    <span className="truncate max-w-[160px]" title={`${a.entity_type} · ${a.action}`}>
                                      {a.entity_type || 'system'} · {(a.action || '').split('.').pop()}
                                    </span>
                                    <span className="font-bold ml-2">{a.count}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODULES */}
      {!loading && activeTab === 'modules' && modules && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-slate-200 rounded-md p-4" data-testid="ua-modules-actions">
            <h3 className="font-display font-bold text-sm mb-3">By Actions (historical)</h3>
            {modules.by_actions.slice(0, 15).map(m => {
              const maxA = modules.by_actions[0]?.action_count || 1;
              return (
                <div key={m.module} className="mb-2">
                  <div className="flex justify-between text-xs">
                    <span className="font-medium capitalize">{m.module || 'Other'}</span>
                    <span className="text-slate-500">{fmt(m.action_count)} · {m.unique_users} users</span>
                  </div>
                  <Bar value={m.action_count} max={maxA} />
                </div>
              );
            })}
          </div>
          <div className="border border-slate-200 rounded-md p-4" data-testid="ua-modules-browse">
            <h3 className="font-display font-bold text-sm mb-3">
              By Browse Activity
              <span className="ml-2 text-[10px] font-normal text-amber-600">Growing from today</span>
            </h3>
            {modules.by_api_calls.length === 0 ? (
              <div className="text-xs text-slate-400 py-8 text-center">
                Module browse tracking started today — data will appear as users navigate the ERP.
              </div>
            ) : modules.by_api_calls.slice(0, 15).map(m => {
              const maxC = modules.by_api_calls[0]?.api_calls || 1;
              return (
                <div key={m.module} className="mb-2">
                  <div className="flex justify-between text-xs">
                    <span className="font-medium">{m.module}</span>
                    <span className="text-slate-500">{fmt(m.api_calls)} calls · {m.unique_users} users · {m.active_days}d</span>
                  </div>
                  <Bar value={m.api_calls} max={maxC} colour="#059669" />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* DEPARTMENTS */}
      {!loading && activeTab === 'departments' && (
        <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="ua-depts-table">
          <table className="w-full text-xs">
            <thead className="bg-[#3b4872] text-white">
              <tr>
                <th className="px-3 py-2 text-left">Department</th>
                <th className="px-3 py-2 text-center">Active Users</th>
                <th className="px-3 py-2 text-center">Logins</th>
                <th className="px-3 py-2 text-center">Actions</th>
                <th className="px-3 py-2 text-center">Modules</th>
                <th className="px-3 py-2 text-left w-28">Engagement</th>
              </tr>
            </thead>
            <tbody>
              {depts.map(d => {
                const maxAct = depts[0]?.total_actions || 1;
                return (
                  <tr key={d.department} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-semibold">{d.department || 'Unknown'}</td>
                    <td className="px-3 py-2 text-center">{d.unique_users}</td>
                    <td className="px-3 py-2 text-center">{fmt(d.login_count)}</td>
                    <td className="px-3 py-2 text-center font-bold text-[#3b4872]">{fmt(d.total_actions)}</td>
                    <td className="px-3 py-2 text-center">{d.modules_used}</td>
                    <td className="px-3 py-2"><Bar value={d.total_actions} max={maxAct} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* LOGIN TREND */}
      {!loading && activeTab === 'trend' && (
        <div className="border border-slate-200 rounded-md p-4" data-testid="ua-trend">
          <h3 className="font-display font-bold text-sm mb-4">Daily Login Activity</h3>
          {trend.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-8">No login data for this period</div>
          ) : (
            <>
              <div className="overflow-x-auto mb-4">
                <div
                  className="flex items-end gap-0.5"
                  style={{ minWidth: `${trend.length * 20}px`, height: '120px' }}
                >
                  {trend.map(t => {
                    const maxL = Math.max(...trend.map(x => x.logins), 1);
                    const h = Math.max(Math.round((t.logins / maxL) * 100), 4);
                    return (
                      <div
                        key={t.date}
                        className="flex-1 flex flex-col items-center h-full"
                        title={`${t.date}: ${t.logins} logins (${t.unique_users} users)`}
                      >
                        <div
                          className="w-full rounded-t-sm bg-[#3b4872] hover:bg-[#2d3856] transition-colors cursor-default"
                          style={{ height: `${h}%` }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-[9px] text-slate-400 mt-1">
                  <span>{trend[0]?.date}</span>
                  <span>{trend[Math.floor(trend.length / 2)]?.date}</span>
                  <span>{trend[trend.length - 1]?.date}</span>
                </div>
              </div>
              <table className="w-full text-xs border border-slate-200 rounded-md overflow-hidden">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Date</th>
                    <th className="px-3 py-2 text-center font-medium text-slate-600">Logins</th>
                    <th className="px-3 py-2 text-center font-medium text-slate-600">Unique Users</th>
                  </tr>
                </thead>
                <tbody>
                  {[...trend].reverse().slice(0, 14).map(t => (
                    <tr key={t.date} className="border-t border-slate-100">
                      <td className="px-3 py-1.5">{t.date}</td>
                      <td className="px-3 py-1.5 text-center font-bold text-[#3b4872]">{t.logins}</td>
                      <td className="px-3 py-1.5 text-center">{t.unique_users}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
